import { Card, Title, Text, Flex, Button } from '@tremor/react';
import type {
  HealthResponse,
  QueueStatsResponse,
  ClassifierStatsResponse,
} from '../types';

interface AlertsPanelProps {
  health: HealthResponse | null;
  queueStats: QueueStatsResponse | null;
  classifierStats: ClassifierStatsResponse | null;
  onRetryFailed: () => void;
  onViewFailed: () => void;
}

interface Alert {
  id: string;
  type: 'error' | 'warning' | 'info';
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function AlertsPanel({
  health,
  queueStats,
  classifierStats,
  onViewFailed,
}: AlertsPanelProps) {
  const alerts = generateAlerts({
    health,
    queueStats,
    classifierStats,
    onViewFailed,
  });

  if (alerts.length === 0) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Flex justifyContent="between" alignItems="center">
          <Title className="text-gray-100">Alerts</Title>
          <Text className="text-emerald-400 text-sm">All systems healthy</Text>
        </Flex>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900 ring-gray-800">
      <Title className="text-gray-100">Alerts</Title>

      <div className="mt-3 space-y-2">
        {alerts.map((alert) => (
          <AlertRow key={alert.id} alert={alert} />
        ))}
      </div>
    </Card>
  );
}

function AlertRow({ alert }: { alert: Alert }) {
  const colorMap = {
    error: 'rose',
    warning: 'amber',
    info: 'blue',
  } as const;

  const iconMap = {
    error: '!',
    warning: '⚠',
    info: 'ℹ',
  };

  return (
    <div
      className={`flex items-center justify-between rounded-lg px-3 py-2 ${
        alert.type === 'error'
          ? 'bg-red-950/30 border border-red-900/50'
          : alert.type === 'warning'
          ? 'bg-amber-950/30 border border-amber-900/50'
          : 'bg-blue-950/30 border border-blue-900/50'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`text-sm ${
            alert.type === 'error'
              ? 'text-red-400'
              : alert.type === 'warning'
              ? 'text-amber-400'
              : 'text-blue-400'
          }`}
        >
          {iconMap[alert.type]}
        </span>
        <Text
          className={
            alert.type === 'error'
              ? 'text-red-300'
              : alert.type === 'warning'
              ? 'text-amber-300'
              : 'text-blue-300'
          }
        >
          {alert.message}
        </Text>
      </div>

      {alert.action && (
        <Button
          size="xs"
          variant="secondary"
          color={colorMap[alert.type]}
          onClick={alert.action.onClick}
        >
          {alert.action.label}
        </Button>
      )}
    </div>
  );
}

function generateAlerts({
  health,
  queueStats,
  classifierStats,
  onViewFailed,
}: {
  health: HealthResponse | null;
  queueStats: QueueStatsResponse | null;
  classifierStats: ClassifierStatsResponse | null;
  onViewFailed: () => void;
}): Alert[] {
  const alerts: Alert[] = [];

  // System health
  if (health?.status !== 'healthy') {
    alerts.push({
      id: 'system-unhealthy',
      type: 'error',
      message: 'System is unhealthy - check Redis connection',
    });
  }

  if (health?.redis !== 'connected') {
    alerts.push({
      id: 'redis-disconnected',
      type: 'error',
      message: 'Redis is disconnected',
    });
  }

  // Failed jobs
  if (queueStats) {
    const totalFailed = Object.values(queueStats.queues).reduce(
      (sum, q) => sum + q.failed,
      0
    );
    if (totalFailed > 0) {
      alerts.push({
        id: 'failed-jobs',
        type: 'error',
        message: `${totalFailed} failed job${totalFailed > 1 ? 's' : ''} detected`,
        action: {
          label: 'View',
          onClick: onViewFailed,
        },
      });
    }
  }

  // Queue backlog
  if (queueStats) {
    const totalWaiting = Object.values(queueStats.queues).reduce(
      (sum, q) => sum + q.waiting,
      0
    );
    if (totalWaiting > 500) {
      alerts.push({
        id: 'queue-backlog',
        type: 'warning',
        message: `Large queue backlog: ${totalWaiting} jobs waiting`,
      });
    }
  }

  // Rate limits
  if (classifierStats?.rateLimits.isCurrentlyRateLimited) {
    alerts.push({
      id: 'rate-limited',
      type: 'warning',
      message: 'OpenAI rate limits active - consider reducing throughput',
    });
  }

  // Low reservoir
  if (classifierStats && classifierStats.limiter.reservoir !== null && classifierStats.limiter.reservoir < 100) {
    alerts.push({
      id: 'low-reservoir',
      type: 'warning',
      message: `Rate limit reservoir low: ${classifierStats.limiter.reservoir} remaining`,
    });
  }

  // High queue in limiter (use classifierStats.limiter which has the same info)
  if (classifierStats && classifierStats.limiter.queued > 100) {
    alerts.push({
      id: 'limiter-queued',
      type: 'info',
      message: `${classifierStats.limiter.queued} requests queued for OpenAI - may need to increase RPM`,
    });
  }

  return alerts;
}
